import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";

export default function PublicPackage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/packages/${slug}`);
        if (alive) setPkg(data);
      } catch (err) {
        toast.error(err.response?.status === 404 ? "Package not found" : formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buy = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Fill in all fields");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/packages/${slug}/buy`, form);
      // Token already in cookie + returned; store for axios as well
      localStorage.setItem("access_token", data.access_token);
      toast.success("Account created — redirecting to checkout…");
      const { data: co } = await api.post("/payments/checkout", {
        invoice_id: data.invoice_id,
        origin_url: window.location.origin,
      });
      window.location.href = co.url;
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(detail);
      if (detail?.toLowerCase().includes("already registered")) {
        setTimeout(() => navigate(`/login?next=/p/${slug}`), 1200);
      }
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500 tracker">Loading…</div>;
  }
  if (!pkg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="font-display text-4xl mb-3">404</div>
        <div className="text-zinc-400 mb-6">This package doesn't exist or has been archived.</div>
        <a href="/" className="btn-primary">Back to Traina</a>
      </div>
    );
  }

  const perSession = (Number(pkg.price) / Number(pkg.sessions_included)).toFixed(2);
  const currency = (pkg.currency || "usd").toUpperCase();

  return (
    <div className="min-h-screen kinetic-glow" data-testid="public-package-page">
      <div className="max-w-5xl mx-auto px-6 py-8 sm:py-12">
        <a href="/" className="flex items-center gap-3 mb-12" data-testid="public-package-home">
          <Logo />
          <span className="font-display text-xl">Traina</span>
        </a>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12">
          {/* Left: package detail */}
          <div className="space-y-6">
            <div>
              <div className="tracker text-brand mb-3">[ Train with {pkg.trainer.name || "your coach"} ]</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="public-package-name">{pkg.name}</h1>
              <p className="text-zinc-400 mt-4 text-base">{pkg.description || `${pkg.sessions_included} dedicated 1:1 sessions, redeemable on your schedule.`}</p>
            </div>

            <div className="card-flat p-6 space-y-4">
              <div className="flex items-baseline gap-3">
                <div className="font-display text-5xl money" data-testid="public-package-price">{currency} {Number(pkg.price).toFixed(2)}</div>
                <div className="tracker text-zinc-500 text-[10px]">one-time payment</div>
              </div>
              <div className="text-sm text-zinc-400">
                <span className="font-mono money">{currency} {perSession}</span>/session · {pkg.sessions_included} sessions included
              </div>
              <ul className="space-y-2 pt-2 border-t border-white/10">
                <li className="flex items-start gap-2 text-sm"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> {pkg.sessions_included} × {pkg.session_type} with {pkg.trainer.name}</li>
                <li className="flex items-start gap-2 text-sm"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> Sessions credited to your training pot on payment</li>
                <li className="flex items-start gap-2 text-sm"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> Book any time via your Traina account</li>
                <li className="flex items-start gap-2 text-sm"><Check size={14} weight="bold" className="text-emerald-400 mt-0.5 shrink-0" /> Email reminders 24h before every session</li>
              </ul>
            </div>

            <div className="tracker text-zinc-500 text-[10px] flex items-center gap-2">
              <Lock size={12} weight="bold" /> Secure checkout via Stripe. You can cancel anytime.
            </div>
          </div>

          {/* Right: signup + pay */}
          <form onSubmit={buy} className="card-flat p-6 sm:p-8 self-start sticky top-8 space-y-4" data-testid="public-package-form">
            <div>
              <div className="tracker text-brand mb-2">[ Get started ]</div>
              <h2 className="font-display text-2xl">Create your account.</h2>
              <p className="text-zinc-500 text-xs mt-1">One-step signup. We'll redirect you to secure payment, then to your dashboard.</p>
            </div>

            <div>
              <label className="tracker block mb-2">Full name</label>
              <input required type="text" value={form.name} onChange={set("name")} className="w-full px-3 py-2.5 border" data-testid="public-form-name" placeholder="Alex Morgan" />
            </div>
            <div>
              <label className="tracker block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={set("email")} className="w-full px-3 py-2.5 border" data-testid="public-form-email" placeholder="you@example.com" />
            </div>
            <div>
              <label className="tracker block mb-2">Password</label>
              <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="w-full px-3 py-2.5 border" data-testid="public-form-password" placeholder="Min 6 characters" />
            </div>
            <div>
              <label className="tracker block mb-2">Phone <span className="text-zinc-600 lowercase">(optional)</span></label>
              <input type="tel" value={form.phone} onChange={set("phone")} className="w-full px-3 py-2.5 border" data-testid="public-form-phone" placeholder="+44…" />
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center !py-3" data-testid="public-form-submit">
              {busy ? "Redirecting…" : <>Pay <span className="money">{currency} {Number(pkg.price).toFixed(2)}</span> & start <ArrowRight size={14} weight="bold" /></>}
            </button>

            <div className="text-center text-[10px] tracker text-zinc-500">
              Already have an account? <a href={`/login?next=/p/${slug}`} className="text-brand-light hover:underline">Sign in</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
